import React, { useCallback, useEffect, useState } from "react";
import "../styles/TestPhaseAnnouncement.css";
import KlausurVideo from "../Vids 4 Marketing/finalos1.web.mp4";
import SimilarTasksVideo from "../Vids 4 Marketing/Comp 2.web.mp4";
import { AUTH_CHANGE_EVENT, isAuthenticated } from "../utils/authSession";

const ADMIN_ALWAYS_POPUP_USER_ID = "7fc17ebf-c7ab-48a0-8393-1e905bb1ab67";
const ANNOUNCEMENT_WINDOW_START_MS = new Date(2026, 5, 29).getTime();
const ANNOUNCEMENT_WINDOW_END_MS = new Date(2026, 6, 2).getTime();

const getStoredUser = () => ({
  id: localStorage.getItem("userID") || "",
  role: localStorage.getItem("userRole") || "STUDENT",
});

const isAnnouncementWindowActive = () => {
  const now = Date.now();
  return now >= ANNOUNCEMENT_WINDOW_START_MS && now < ANNOUNCEMENT_WINDOW_END_MS;
};

const shouldShowAnnouncement = () => {
  if (!isAuthenticated()) return false;

  const user = getStoredUser();
  const userId = user.id.toLowerCase();
  if (!userId) return false;
  if (userId === ADMIN_ALWAYS_POPUP_USER_ID) return true;
  if (user.role !== "STUDENT") return false;

  return isAnnouncementWindowActive();
};

function TestPhaseAnnouncement() {
  const [isOpen, setIsOpen] = useState(false);
  const [dismissedUserId, setDismissedUserId] = useState("");

  const refreshVisibility = useCallback(() => {
    const user = getStoredUser();
    const normalizedUserId = user.id.toLowerCase();

    if (!shouldShowAnnouncement()) {
      setIsOpen(false);
      return;
    }

    if (normalizedUserId && normalizedUserId === dismissedUserId) return;
    setIsOpen(true);
  }, [dismissedUserId]);

  useEffect(() => {
    refreshVisibility();

    const onStorage = () => refreshVisibility();
    const onAuthChanged = () => {
      setDismissedUserId("");
      window.setTimeout(refreshVisibility, 0);
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener(AUTH_CHANGE_EVENT, onAuthChanged);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(AUTH_CHANGE_EVENT, onAuthChanged);
    };
  }, [refreshVisibility]);

  useEffect(() => {
    if (!isOpen) return undefined;

    document.documentElement.classList.add("announcement-modal-open");
    document.body.classList.add("announcement-modal-open");
    return () => {
      document.documentElement.classList.remove("announcement-modal-open");
      document.body.classList.remove("announcement-modal-open");
    };
  }, [isOpen]);

  const closeAnnouncement = useCallback(() => {
    const user = getStoredUser();
    const normalizedUserId = user.id.toLowerCase();

    setDismissedUserId(normalizedUserId);
    setIsOpen(false);
  }, []);

  if (!isOpen) return null;

  return (
    <div
      className="test-phase-announcement-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="test-phase-announcement-title"
    >
      <article className="test-phase-announcement-card">
        <button
          type="button"
          className="test-phase-announcement-close"
          onClick={closeAnnouncement}
          aria-label="Hinweis schliessen"
        >
          x
        </button>

        <div className="test-phase-announcement-scroll">
          <header className="test-phase-announcement-header">
            <span className="test-phase-announcement-badge">
              TESTPHASE 2
            </span>
            <h2 id="test-phase-announcement-title">
              Die zweite Testphase ist gestartet
            </h2>
            <p>
              Vielen Dank f&uuml;r eure Teilnahme an der ersten Testphase. Wir
              testen jetzt die n&auml;chsten KI-Werkzeuge f&uuml;r eine st&auml;rkere
              Pr&uuml;fungsvorbereitung in LearnHut.
            </p>
          </header>

          <div className="test-phase-announcement-divider">
            <span>Neue Funktionen</span>
          </div>

          <section className="test-phase-announcement-feature test-phase-announcement-feature-primary">
            <div className="test-phase-announcement-video-shell">
              <video
                src={KlausurVideo}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
              />
              <span className="test-phase-announcement-video-label">
                Klausur-Demo
              </span>
            </div>
            <div className="test-phase-announcement-copy">
              <span className="test-phase-announcement-kicker">
                Praxis-Modus
              </span>
              <h3>Klausuren generieren</h3>
              <p>
                Erstelle neue &Uuml;bungsklausuren direkt im Praxisbereich. Die KI
                nutzt vorhandene Aufgaben und Altklausuren, damit du mit
                realistischen Klausurvarianten trainieren kannst.
              </p>
              <div className="test-phase-announcement-tags" aria-hidden="true">
                <span>KI-gest&uuml;tzt</span>
                <span>Mit Musterl&ouml;sungen</span>
              </div>
            </div>
          </section>

          <section className="test-phase-announcement-feature test-phase-announcement-feature-secondary">
            <div className="test-phase-announcement-video-shell">
              <video
                src={SimilarTasksVideo}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
              />
              <span className="test-phase-announcement-video-label">
                Aufgaben-Demo
              </span>
            </div>
            <div className="test-phase-announcement-copy">
              <span className="test-phase-announcement-kicker">
                &Uuml;ben mit Varianten
              </span>
              <h3>&Auml;hnliche Aufgaben generieren</h3>
              <p>
                &Ouml;ffne eine bestehende Aufgabe und lasse dir eine neue,
                &auml;hnliche Aufgabe mit angepassten Werten und L&ouml;sungsweg
                erzeugen.
              </p>
            </div>
          </section>
        </div>
      </article>
    </div>
  );
}

export default TestPhaseAnnouncement;
